import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { Edit3, Save, Download, Loader2, Check, ImagePlus, X, RotateCcw } from 'lucide-react';
import {
  MONTHS, compact, money, moneyFull, intFull, dec, change, RichText, pageLabel, editBox, TextField, NumberField,
} from './bulletinShared';
import BulletinDeck from './BulletinDeck';
import BulletinMix from './BulletinMix';

// Yataş Grubu — Dijital Pazarlama Aylık Bülten.
// Tasarım, markanın daha önce elle hazırlayıp gönderdiği "Enza Home Aylık Bülten" PDF'inin
// birebir karşılığı. Sayılar backend'den (/api/bulletin) otomatik gelir; ayın kampanyası,
// kreatifler, özet ve gelecek ay planı marka ekibi tarafından "Düzenle" ile doldurulur.

const API = 'https://api.glowytics.com';
const ASSET = 'https://api.glowytics.com/pixel';

const THEMES = {
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e': {
    name: 'Enza Home', primary: '#203764',
    logoLight: `${ASSET}/enza-assets/enza-logo-beyaz.svg`, logoDark: `${ASSET}/enza-assets/enza-logo.svg`,
  },
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7': {
    name: 'Yataş Bedding', primary: '#0D1B3E',
    logoLight: `${ASSET}/yatas-bedding-logo-beyaz.png`, logoDark: `${ASSET}/yatas-bedding-logo-lacivert.png`,
  },
};
const DEFAULT_THEME = { primary: '#1F2A44', logoLight: null, logoDark: null };

// ── Sayfa ───────────────────────────────────────────────────────────────────
export default function MonthlyBulletin() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const theme = THEMES[brandId] || { ...DEFAULT_THEME, name: selectedBrand?.name };

  const now = new Date();
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [data, setData] = useState(null);
  const [manual, setManual] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  // Üç görünüm vardı: tek sayfa (Enza PDF'i) / sunum (Yataş Bedding PPTX'i) / karma. Yataş karma ile ilerleme kararı
  // aldı (13 Eki); 16 Eki seçici kaldırıldı, yalnız karma görünür. Diğer şablonlar kodda duruyor.
  const template = 'mix';
  // Düzenleme yalnız markanın OWNER/ADMIN'lerine (Esra ve ekibi); MARKETER ve diğerleri bülteni sadece okur.
  const canEdit = ['OWNER', 'ADMIN'].includes(selectedBrand?.currentUserRole);
  useEffect(() => { if (!canEdit) setEditing(false); }, [canEdit]);
  const fileRef = useRef(null);
  const slotRef = useRef(0);
  const reqRef = useRef(0); // ay hızlı değişirse geç dönen eski yanıt yenisini ezmesin

  // PDF'teki yazı karakterleri
  useEffect(() => {
    const id = 'bulten-fonts';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Open+Sans:wdth,wght@75..100,400..700&family=Roboto:wght@500;700&display=swap';
    document.head.appendChild(l);
  }, []);

  const load = useCallback(async () => {
    if (!brandId) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/bulletin?year=${year}&month=${month}`, {
        credentials: 'include', headers: { 'X-Brand-ID': brandId },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Bülten alınamadı');
      if (reqId !== reqRef.current) return;
      setData(json);
      setManual(json.manual || {});
      setDirty(false);
    } catch (e) {
      toast.error(e.message);
      setData(null);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [brandId, year, month]);

  useEffect(() => { load(); }, [load]);

  // manual nesnesinde iç içe alan güncelle: set('campaign.title', 'x')
  const set = useCallback((pathStr, value) => {
    setManual((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const keys = pathStr.split('.');
      let o = next;
      keys.slice(0, -1).forEach((k) => { if (o[k] == null || typeof o[k] !== 'object') o[k] = {}; o = o[k]; });
      const last = keys[keys.length - 1];
      if (value === '' || value == null) delete o[last]; else o[last] = value;
      return next;
    });
    setDirty(true);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/bulletin`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ year, month, manual }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Kaydedilemedi');
      toast.success('Bülten kaydedildi');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const pickImage = (slot) => {
    slotRef.current = slot;
    if (fileRef.current) fileRef.current.accept = slot === 'video' ? 'video/mp4,video/webm,video/quicktime' : 'image/*';
    fileRef.current?.click();
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const slot = slotRef.current;
    if (slot === 'video' && file.size > 35 * 1024 * 1024) { toast.error('Video 35 MB’den büyük; YouTube linki girin.'); return; }
    setUploading(slot);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
      });
      const res = await fetch(`${API}/api/bulletin/upload`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ dataUrl }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Yüklenemedi');
      if (slot === 'video') { set('campaign.video', json.url); return; }
      const imgs = [...(manual.campaign?.images || [null, null, null])];
      imgs[slot] = json.url;
      set('campaign.images', imgs);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(null);
    }
  };
  const removeImage = (slot) => {
    const imgs = [...(manual.campaign?.images || [null, null, null])];
    imgs[slot] = null;
    set('campaign.images', imgs);
  };

  // ── Türetilmiş değerler (elle düzeltme varsa o kazanır) ───────────────────
  const v = useMemo(() => {
    if (!data) return null;
    const cur = data.auto.current;
    const prev = data.auto.previous;
    const ov = manual.overrides || {};
    const pick = (k) => (ov[k] != null ? ov[k] : cur[k]);
    const revenue = pick('revenue');
    const costPerformance = pick('costPerformance');
    return {
      sessions: pick('sessions'), users: pick('users'), impressions: pick('impressions'),
      views: pick('views'), revenue, cost: pick('cost'),
      costBranding: pick('costBranding'), costPerformance,
      roas: costPerformance > 0 ? revenue / costPerformance : cur.roas,
      prev,
    };
  }, [data, manual.overrides]);

  if (!brandId) return <div className="p-8 text-gray-500">Marka seçin.</div>;

  const prevName = MONTHS[(month + 10) % 12];
  const nextName = MONTHS[month % 12];
  const monthName = MONTHS[month - 1];

  const kpis = v ? [
    { key: 'sessions', title: 'Web Trafiği', value: compact(v.sessions), sub: 'Web sitesi ziyaret sayısı', ch: change(v.sessions, v.prev.sessions) },
    { key: 'users', title: 'Toplam Kullanıcı', value: compact(v.users), sub: 'Tekil Ziyaretçi Sayısı', ch: change(v.users, v.prev.users) },
    { key: 'impressions', title: 'Gösterim', value: compact(v.impressions), sub: 'Reklam gösterimi', ch: change(v.impressions, v.prev.impressions) },
    { key: 'views', title: 'İzlenme', value: compact(v.views), sub: 'Reklam İzlenmesi', ch: change(v.views, v.prev.views) },
    { key: 'revenue', title: 'Toplam Gelir', value: money(v.revenue), sub: 'Web sitesi KDV hariç ciro', ch: change(v.revenue, v.prev.revenue) },
    { key: 'cost', title: 'Reklam Harcaması', value: money(v.cost), sub: 'Branding + Performans Bütçesi', ch: change(v.cost, v.prev.cost) },
  ] : [];

  const sessCh = v ? change(v.sessions, v.prev.sessions) : null;
  const autoPerfText = v
    ? `**${money(v.costPerformance)}** bütçe ile **${money(v.revenue)}** gelir (kdv hariç ciro) elde edildi.\n**ROAS ${dec(v.roas)}** seviyesinde tamamlandı.`
    : '';
  const autoBrandText = v
    ? `**${money(v.costBranding)}** bütçe ile **${compact(v.impressions)}** reklam gösterim sağlandı.\n`
      + `Web sitesi trafiği bir önceki aya göre **%${Math.abs(Math.round(sessCh || 0))}** ${sessCh >= 0 ? 'artışla' : 'düşüşle'} `
      + `**${compact(v.sessions)}** ziyaret sayısı olarak gerçekleşti.`
    : '';

  const auto = data?.auto;
  const hl = manual.highlights || {};
  const channelOpts = auto?.channels || [];
  const channel = channelOpts.find((c) => c.label === hl.channel) || channelOpts[0];
  const productOpts = auto?.products || [];
  const products = (hl.products && hl.products.length ? hl.products : productOpts.slice(0, 3).map((p) => p.name));
  const pageOpts = auto?.pages || [];
  const pages = (hl.pages && hl.pages.length
    ? hl.pages
    : pageOpts.slice(0, 3).map((p) => ({ path: p.path, label: pageLabel(p) })));

  const camp = manual.campaign || {};
  const images = camp.images || [null, null, null];
  const campStats = camp.stats || {};
  const video = auto?.video;

  const nx = manual.next || {};
  const nt = auto?.nextTargets || {};
  const tgt = (k) => (nx.targets?.[k] != null ? nx.targets[k] : nt[k]);
  const nextChannels = nx.channels || (auto?.activeChannels || []).join(', ');

  const card = 'rounded-[14px] text-white text-center px-2 py-3 flex flex-col justify-between';
  const sectionTitle = 'text-[25px] font-bold text-black pb-2 mb-4 border-b-[4px] border-[#7f7f7f]';
  const greyBox = 'bg-[#f1f1f1] rounded-[18px] border-l-[7px]';

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-3 sm:px-6">
      <style>{`
        #bulten-print { font-family: 'Open Sans', system-ui, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        #bulten-print .bt-h { font-family: 'Roboto', 'Open Sans', sans-serif; }
        #bulten-print .bt-cond { font-stretch: 87.5%; }
        @media print {
          @page { size: A4 ${template === 'sheet' ? 'portrait' : 'landscape'}; margin: 6mm; }
          body * { visibility: hidden !important; }
          #bulten-print, #bulten-print * { visibility: visible !important; }
          #bulten-print { position: absolute; left: 0; top: 0; width: 1150px; zoom: 0.66; box-shadow: none !important; margin: 0 !important; }
          #bulten-print .bt-section { break-inside: avoid; }
          #bulten-print .bt-noprint { display: none !important; }
        }
        @media print {
          #bulten-print.bt-deck { width: 1280px; zoom: 0.84; background: none; }
          #bulten-print.bt-deck .bt-slide-wrap { height: 720px !important; }
          #bulten-print.bt-deck .bt-slide { transform: none !important; }
          #bulten-print.bt-deck .bt-section { break-after: page; margin: 0 !important; }
        }
      `}</style>

      {/* Araç çubuğu */}
      <div className={`${template === 'sheet' ? 'max-w-[1150px]' : 'max-w-[1280px]'} mx-auto mb-4 flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 px-4 py-3 shadow-sm`}>
        <div className="mr-auto">
          <div className="text-base font-semibold text-gray-900">Aylık Bülten</div>
          <div className="text-xs text-gray-500">
            Sayılar otomatik dolar{canEdit && ' · kampanya, kreatif ve gelecek ay planı elle girilir'}
            {canEdit && data?.meta?.updatedAt && <> · son düzenleme {new Date(data.meta.updatedAt).toLocaleString('tr-TR')} ({data.meta.updatedBy})</>}
          </div>
        </div>
        <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))} disabled={dirty}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} disabled={dirty}>
          {[now.getFullYear() - 1, now.getFullYear()].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {canEdit && <button
          onClick={() => setEditing((x) => !x)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${editing ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          {editing ? <Check size={15} /> : <Edit3 size={15} />} {editing ? 'Düzenlemeyi bitir' : 'Düzenle'}
        </button>}
        {dirty && (
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white" style={{ background: theme.primary }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Kaydet
          </button>
        )}
        <button onClick={() => window.print()} disabled={dirty || editing} title={dirty ? 'Önce kaydedin' : ''} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
          <Download size={15} /> PDF
        </button>
      </div>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />

      {loading && !data && (
        <div className="max-w-[1150px] mx-auto bg-white rounded-xl p-16 flex items-center justify-center text-gray-500 gap-2">
          <Loader2 className="animate-spin" size={18} /> Bülten hazırlanıyor…
        </div>
      )}

      {data && v && template === 'deck' && (
        <div id="bulten-print" lang="tr" className={`bt-deck max-w-[1280px] mx-auto ${loading ? 'opacity-60' : ''}`}>
          <BulletinDeck data={data} manual={manual} set={set} editing={editing} year={year} month={month} />
        </div>
      )}

      {data && v && template === 'mix' && (
        <div id="bulten-print" lang="tr" className={`bt-deck max-w-[1280px] mx-auto ${loading ? 'opacity-60' : ''}`}>
          <BulletinMix data={data} manual={manual} set={set} editing={editing} year={year} month={month}
            theme={theme} pickImage={pickImage} removeImage={removeImage} uploading={uploading} />
        </div>
      )}

      {data && v && template === 'sheet' && (
        <div id="bulten-print" lang="tr" className={`max-w-[1150px] mx-auto bg-white shadow-sm ${loading ? 'opacity-60' : ''}`}>
          {/* Başlık */}
          <div className="flex items-center justify-between px-7 py-9" style={{ background: theme.primary }}>
            {theme.logoLight
              ? <img src={theme.logoLight} alt={theme.name} className="h-8 sm:h-10 object-contain" />
              : <span className="text-white text-3xl font-semibold">{theme.name}</span>}
            <div className="bt-h text-white text-lg sm:text-[27px] font-bold text-right">
              Dijital Pazarlama Aylık Bülten | {monthName} {year}
            </div>
          </div>

          <div className="px-4 sm:px-7 pt-6 pb-8">
            {/* Genel performans */}
            <section className="bt-section mb-9">
              <h2 className={`bt-h ${sectionTitle}`}>{monthName} Ayı Genel Performans</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {kpis.map((k) => (
                  <div key={k.key}>
                    <div className={card} style={{ background: theme.primary, minHeight: 158 }}>
                      <div className="text-[18px] whitespace-nowrap">{k.title}</div>
                      <div className="text-[38px] leading-tight">{k.value}</div>
                      <div className="text-[15px] bt-cond">
                        {k.ch == null ? '' : `${k.ch >= 0 ? '▲' : '▼'}  %${Math.round(k.ch)} (${prevName})`}
                      </div>
                      <div className="text-[14px] leading-tight opacity-95 bt-cond">{k.sub}</div>
                    </div>
                    {editing && (
                      <NumberField
                        editing value={manual.overrides?.[k.key]} className="mt-1"
                        placeholder={`Elle düzelt (oto: ${Math.round(data.auto.current[k.key])})`}
                        onChange={(x) => set(`overrides.${k.key}`, x)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Kampanya performansı */}
            <section className="bt-section mb-9">
              <h2 className={`bt-h ${sectionTitle}`}>Kampanya Performansı</h2>
              <div className={`grid gap-5 ${data.allBranding ? '' : 'md:grid-cols-2'}`}>
                {!data.allBranding && (
                  <div className={`${greyBox} p-5 min-h-[200px]`} style={{ borderColor: theme.primary }}>
                    <div className="text-[22px] font-semibold text-[#333] mb-6">Performans/Satış Reklamları</div>
                    <div className="text-[18px] text-[#444] leading-relaxed"><RichText text={manual.perfText || autoPerfText} /></div>
                    {editing && (
                      <>
                        <NumberField editing value={manual.overrides?.costPerformance} className="mt-3"
                          placeholder={`Performans bütçesi (oto: ${Math.round(data.auto.current.costPerformance)})`}
                          onChange={(x) => set('overrides.costPerformance', x)} />
                        <TextField editing multiline rows={3} className="mt-2" value={manual.perfText}
                          placeholder="Boş bırakılırsa metin otomatik yazılır" onChange={(x) => set('perfText', x)} />
                      </>
                    )}
                  </div>
                )}
                <div className={`${greyBox} p-5 min-h-[200px]`} style={{ borderColor: theme.primary }}>
                  <div className="text-[22px] font-semibold text-[#333] mb-6">Branding/Tanıtım Reklamları</div>
                  <div className="text-[18px] text-[#444] leading-relaxed text-justify"><RichText text={manual.brandText || autoBrandText} /></div>
                  {editing && (
                    <>
                      <NumberField editing value={manual.overrides?.costBranding} className="mt-3"
                        placeholder={`Branding bütçesi (oto: ${Math.round(data.auto.current.costBranding)})`}
                        onChange={(x) => set('overrides.costBranding', x)} />
                      <TextField editing multiline rows={3} className="mt-2" value={manual.brandText}
                        placeholder="Boş bırakılırsa metin otomatik yazılır" onChange={(x) => set('brandText', x)} />
                    </>
                  )}
                </div>
              </div>
            </section>

            {/* Ayın öne çıkanları */}
            <section className="bt-section mb-9">
              <h2 className={`bt-h ${sectionTitle}`}>Ayın Öne Çıkanları</h2>
              <div className="grid md:grid-cols-3 gap-5">
                <div className={`${greyBox} px-4 py-5 text-center text-[18px] text-[#444] leading-snug`} style={{ borderColor: theme.primary }}>
                  <div className="font-semibold">En Çok Trafik Getiren Kanal</div>
                  <div className="font-semibold">{channel?.label || '–'}</div>
                  <div>{channel ? intFull(channel.sessions) : ''}</div>
                  <div>web sitesi ziyareti</div>
                  {editing && channelOpts.length > 0 && (
                    <select className="mt-2 w-full text-sm border border-amber-300 rounded px-2 py-1 bg-white" value={channel?.label || ''}
                      onChange={(e) => set('highlights.channel', e.target.value)}>
                      {channelOpts.map((c) => <option key={c.label} value={c.label}>{c.label} — {intFull(c.sessions)}</option>)}
                    </select>
                  )}
                </div>
                <div className={`${greyBox} px-4 py-5 text-center text-[18px] text-[#444] leading-snug`} style={{ borderColor: theme.primary }}>
                  <div className="font-semibold">En Çok Satılan Ürünler / Web Sitesi</div>
                  {products.map((p, i) => <div key={i}>{p}</div>)}
                  {editing && [0, 1, 2].map((i) => (
                    <select key={i} className="mt-1.5 w-full text-sm border border-amber-300 rounded px-2 py-1 bg-white" value={products[i] || ''}
                      onChange={(e) => { const arr = [...products]; arr[i] = e.target.value; set('highlights.products', arr); }}>
                      {productOpts.map((p) => <option key={p.name} value={p.name}>{p.name} — {p.qty} adet · {money(p.revenue)}</option>)}
                    </select>
                  ))}
                </div>
                <div className={`${greyBox} px-4 py-5 text-center text-[18px] text-[#444] leading-snug`} style={{ borderColor: theme.primary }}>
                  <div className="font-semibold">En Yüksek Trafikli Sayfalar</div>
                  {pages.map((p, i) => <div key={i}>{p.label}</div>)}
                  {!pages.length && <div className="text-sm text-gray-400">{auto.pagesError || 'Veri yok'}</div>}
                  {editing && [0, 1, 2].map((i) => (
                    <div key={i} className="mt-1.5 flex gap-1">
                      <select className="w-1/2 text-xs border border-amber-300 rounded px-1 py-1 bg-white" value={pages[i]?.path || ''}
                        onChange={(e) => {
                          const po = pageOpts.find((x) => x.path === e.target.value);
                          const arr = pages.map((x) => ({ ...x })); arr[i] = { path: po.path, label: pageLabel(po) };
                          set('highlights.pages', arr);
                        }}>
                        {pageOpts.map((p) => <option key={p.path} value={p.path}>{p.path} — {intFull(p.views)}</option>)}
                      </select>
                      <input className="w-1/2 text-xs border border-amber-300 rounded px-1 py-1" value={pages[i]?.label || ''}
                        onChange={(e) => { const arr = pages.map((x) => ({ ...x })); arr[i] = { ...arr[i], label: e.target.value }; set('highlights.pages', arr); }} />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Ayın kampanyası */}
            <section className="bt-section mb-8">
              <h2 className={`bt-h ${sectionTitle}`}>
                {camp.title || (editing ? '' : `${monthName} Kampanyası`)}
                <TextField editing={editing} value={camp.title} placeholder="Kampanya başlığı (ör. En Şık Yaz Enza’da)"
                  onChange={(x) => set('campaign.title', x)} inputClass="text-lg font-semibold" />
              </h2>
              <div className="bg-[#e3e3e3] rounded-[18px] p-3 sm:p-4">
                <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1.7fr] gap-4 mb-4">
                  {[0, 1, 2].map((i) => {
                    const src = images[i];
                    if (!src && !editing) return null;
                    return (
                      <div key={i} className={`relative bg-white/60 overflow-hidden ${i === 2 ? 'col-span-2 md:col-span-1' : ''} ${editing ? editBox : ''}`}
                        style={{ aspectRatio: i === 2 ? '1256 / 729' : '1 / 1' }}>
                        {src
                          ? <img src={src} alt="" className="w-full h-full object-cover" />
                          : (
                            <button onClick={() => pickImage(i)} className="w-full h-full flex flex-col items-center justify-center text-gray-500 text-sm gap-1">
                              {uploading === i ? <Loader2 className="animate-spin" /> : <ImagePlus />} Kreatif ekle
                            </button>
                          )}
                        {editing && src && (
                          <div className="absolute top-2 right-2 flex gap-1 bt-noprint">
                            <button onClick={() => pickImage(i)} className="bg-white/90 rounded p-1 text-gray-700" title="Değiştir"><ImagePlus size={15} /></button>
                            <button onClick={() => removeImage(i)} className="bg-white/90 rounded p-1 text-red-600" title="Kaldır"><X size={15} /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="bg-white rounded-[16px] px-5 py-4">
                  <div className="text-[19px] font-bold text-[#333] mb-1">Kampanya Özeti</div>
                  <div className="text-[17px] text-[#444] leading-relaxed">
                    {camp.summary ? <RichText text={camp.summary} /> : (!editing && <span className="text-gray-400">Kampanya özeti henüz girilmedi.</span>)}
                  </div>
                  <TextField editing={editing} multiline value={camp.summary} onChange={(x) => set('campaign.summary', x)}
                    placeholder={'Kampanyanın tarihi, mecraları ve sonucu. **kalın** yazmak için iki yıldız kullanın.'} />
                  <div className="grid grid-cols-3 gap-3 mt-5 text-center">
                    {[
                      { k: 'impressions', label: 'TOPLAM REKLAM GÖSTERİMİ', val: compact(campStats.impressions ?? v.impressions) },
                      { k: 'views', label: 'TOPLAM İZLEME', val: compact(campStats.views ?? v.views) },
                      { k: 'completion', label: '%100 İZLENME ORANI', val: (campStats.completion ?? video?.completionRate) != null ? `${dec(campStats.completion ?? video.completionRate)}%` : '–' },
                    ].map((s) => (
                      <div key={s.k}>
                        <div className="text-[13px] sm:text-[20px] font-semibold text-[#666]">{s.label}</div>
                        <div className="text-[20px] font-bold text-[#333]">{s.val}</div>
                        {editing && (
                          <NumberField editing value={campStats[s.k]} className="mt-1" placeholder="Elle düzelt"
                            onChange={(x) => set(`campaign.stats.${s.k}`, x)} />
                        )}
                      </div>
                    ))}
                  </div>
                  {editing && (
                    <TextField editing className="mt-3" value={camp.videoKeyword} onChange={(x) => set('campaign.videoKeyword', x)}
                      placeholder="%100 izlenme oranı yalnız bu kampanyadan hesaplansın (kampanya adında geçen kelime, ör. Fit & Flex) — kaydettikten sonra güncellenir" />
                  )}
                </div>
              </div>
            </section>

            {/* Gelecek ay planı */}
            <section className="bt-section rounded-[18px] text-white px-5 sm:px-6 py-6" style={{ background: theme.primary }}>
              <div className="text-[29px] font-bold mb-2">{nextName} Ayı Planlaması</div>
              <div className="grid sm:grid-cols-[1fr_1.1fr_1.6fr] gap-4 text-[18px] font-bold leading-snug">
                <div>
                  <div>HEDEF</div>
                  {[
                    ['revenue', 'HEDEF CİRO', (x) => money(x)],
                    ['sessions', 'HEDEF TRAFİK', (x) => compact(x)],
                    ['impressions', 'HEDEF GÖSTERİM', (x) => compact(x)],
                    ['views', 'HEDEF İZLENME', (x) => compact(x)],
                  ].map(([k, label, f]) => (
                    <div key={k}>
                      {label} : {tgt(k) != null ? f(tgt(k)) : '–'}
                      {editing && <NumberField editing value={nx.targets?.[k]} className="mt-0.5 mb-1" placeholder={`Oto: ${nt[k] ?? 'girilmemiş'}`} onChange={(x) => set(`next.targets.${k}`, x)} />}
                    </div>
                  ))}
                </div>
                <div>
                  <div>BÜTÇE</div>
                  {!data.allBranding && (
                    <div>
                      PERFORMANS : {tgt('costPerformance') != null ? moneyFull(tgt('costPerformance')) : '–'}
                      {editing && <NumberField editing value={nx.targets?.costPerformance} className="mt-0.5 mb-1" placeholder={`Oto: ${nt.costPerformance ?? 'girilmemiş'}`} onChange={(x) => set('next.targets.costPerformance', x)} />}
                    </div>
                  )}
                  <div>
                    BRANDING : {tgt('costBranding') != null ? moneyFull(tgt('costBranding')) : '–'}
                    {editing && <NumberField editing value={nx.targets?.costBranding} className="mt-0.5 mb-1" placeholder={`Oto: ${nt.costBranding ?? 'girilmemiş'}`} onChange={(x) => set('next.targets.costBranding', x)} />}
                  </div>
                </div>
                <div className="text-center">
                  <div>MECRALAR</div>
                  <div className="uppercase text-[17px] leading-snug">{nextChannels}</div>
                  <TextField editing={editing} className="mt-1" value={nx.channels} onChange={(x) => set('next.channels', x)}
                    placeholder="Virgülle ayırın (boşsa bu ay aktif olan mecralar)" />
                </div>
              </div>
              {editing && (
                <div className="text-xs text-amber-200 mt-2 bt-noprint">
                  Hedef ve bütçeler “Target vs Actual” sayfasında girilen {nextName} hedeflerinden gelir. Burada yazılan değer yalnız bültende geçerli olur.
                </div>
              )}
              <div className="mt-5 text-[18px] leading-relaxed">
                {nx.text ? <RichText text={nx.text} /> : (!editing && <span className="opacity-70">Gelecek ay planı henüz girilmedi.</span>)}
              </div>
              <TextField editing={editing} multiline rows={6} className="mt-2" value={nx.text} onChange={(x) => set('next.text', x)}
                placeholder={'Gelecek ayın kampanyaları, mecra stratejisi ve aksiyonlar. **kalın** yazmak için iki yıldız kullanın.'} />
            </section>

            {theme.logoDark && (
              <div className="flex justify-center mt-7">
                <img src={theme.logoDark} alt={theme.name} className="h-7 object-contain" />
              </div>
            )}
          </div>
        </div>
      )}

      {editing && dirty && (
        <div className="fixed bottom-5 right-5 flex gap-2 bt-noprint">
          <button onClick={() => { setManual(data.manual || {}); setDirty(false); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-white border border-gray-300 shadow">
            <RotateCcw size={15} /> Vazgeç
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm text-white shadow" style={{ background: theme.primary }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Kaydet
          </button>
        </div>
      )}
    </div>
  );
}
